/* eslint-disable react-refresh/only-export-components --
   REQ-25 §4.1 legt fest, dass AdminChangesProvider und useAdminChanges in dieser Datei liegen. */
import { createContext, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react';

/**
 * Meldeweg der Administration an die Navigation (REQ-25 §4.1). Die Abschnitte rufen
 * `notifyNavigationChanged()` nach jeder Änderung an Views, Kategorien, Gruppen und
 * Gruppenzuordnungen; `MainLayout` lädt daraufhin die Kategorien neu.
 */
export interface AdminChanges {
  notifyNavigationChanged(): void;
}

// Außerhalb des Providers ist die Meldung wirkungslos — die Abschnitte bleiben damit
// einzeln testbar.
const AdminChangesContext = createContext<AdminChanges>({
  notifyNavigationChanged: () => undefined,
});

export interface AdminChangesProviderProps {
  onNavigationChanged?: () => void;
  children: ReactNode;
}

export function AdminChangesProvider(props: AdminChangesProviderProps) {
  const { onNavigationChanged } = props;

  // Der Wert bleibt über alle Renderzyklen derselbe, auch wenn `onNavigationChanged` als
  // Inline-Funktion hereinkommt — Abschnitte dürfen ihn gefahrlos in Abhängigkeitslisten
  // aufnehmen.
  const latest = useRef(onNavigationChanged);
  useEffect(() => {
    latest.current = onNavigationChanged;
  }, [onNavigationChanged]);

  const value = useMemo<AdminChanges>(
    () => ({
      notifyNavigationChanged: () => {
        latest.current?.();
      },
    }),
    [],
  );

  return (
    <AdminChangesContext.Provider value={value}>{props.children}</AdminChangesContext.Provider>
  );
}

export function useAdminChanges(): AdminChanges {
  return useContext(AdminChangesContext);
}
